import { api } from "@/lib/api";
import {
  mapProviderFromDto,
  ProviderDto,
  providerDtoListSchema,
  ProviderDtoSchema,
} from "@/models";
import { queryOptions, useQuery } from "@tanstack/react-query";
import { useEffect } from "react";
import { useToast } from "../use-toast";

export const getProvidersQueryOptions = queryOptions({
  queryKey: ["providers"],
  queryFn: async () => {
    const result: Array<ProviderDto> = [];
    let page = 0;
    let hasNextPage = true;
    while (hasNextPage) {
      page += 1;
      const res = await api.get("/api/providers", providerDtoListSchema, {
        query: {
          page: page.toString(),
        },
        projection: ["Person"],
      });
      result.push(...res.Data);
      hasNextPage = res.PageInfo.HasNextPage;
    }
    return result.map(mapProviderFromDto);
  },
});

export function useGetProviders<TERROR extends Error>(opts?: {
  onError?: (error: TERROR) => void;
  enabled?: boolean;
}) {
  const { toast } = useToast();

  const q = useQuery({
    ...getProvidersQueryOptions,
    enabled: opts?.enabled,
  });
  useEffect(() => {
    if (!q.error) return;
    toast({
      title: "toast.getProvidersError",
      variant: "destructive",
    });
    opts?.onError?.(q.error as TERROR);
  }, [opts, q.error, toast]);
  return q;
}

export function getProviderQueryOptions(id: string) {
  return queryOptions({
    queryKey: ["providers", id],
    queryFn: () =>
      api
        .get("/api/providers/$id", ProviderDtoSchema, {
          pathReplace: { $id: id },
          projection: ["Person"],
        })
        .then(mapProviderFromDto),
  });
}

export function useGetProvider<TERROR extends Error>(
  id: string,
  opts?: { onError: (error: TERROR) => void },
) {
  const { toast } = useToast();
  const q = useQuery(getProviderQueryOptions(id));
  useEffect(() => {
    if (!q.error) return;
    toast({
      title: "toast.getProviderDetailError",
      variant: "destructive",
    });
    opts?.onError(q.error as TERROR);
  }, [opts, q.error, toast]);
  return q;
}
